import * as THREE from 'three';
import { Utils } from '../utils.js';
import { EventDispatcher } from '../EventDispatcher.js';

export class LibraryMeshTool extends EventDispatcher {
    constructor(viewer) {
        super();

        this.viewer = viewer;
        this.renderer = viewer.renderer;

        this.addEventListener('start_inserting_mesh', (e) => {
            this.viewer.dispatchEvent({
                type: 'cancel_insertions',
            });
        });

        this.scene = new THREE.Scene();
        this.scene.name = 'scene_library_mesh';

        const directionalLight1 = new THREE.DirectionalLight(0xffeeff, 0.8);
        directionalLight1.position.set(1, 1, 1);
        this.scene.add(directionalLight1);

        const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight2.position.set(-1, 0.5, -1);
        this.scene.add(directionalLight2);

        const ambientLight = new THREE.AmbientLight(0xffffee, 0.25);

        this.scene.add(ambientLight);

        this.viewer.inputHandler.registerInteractiveScene(this.scene);

        this.onRemove = (e) => {
            e.mesh.removeEventListener('select');
            e.mesh.removeEventListener('deselect');

            this.scene.remove(e.mesh);
        };

        this.onAdd = (e) => {
            if (!e.mesh._listeners) {
                e.mesh._listeners = {};
            }

            e.mesh.addEventListener('select', (e) => {});
            e.mesh.addEventListener('deselect', (e) => {});

            this.scene.add(e.mesh);

            e.mesh.traverse((child) => {
                child.mainParent = e.mesh;
            });
        };

        for (let mesh of viewer.scene.meshes) {
            this.onAdd({ mesh: mesh });
        }

        viewer.addEventListener('update', this.update.bind(this));
        viewer.addEventListener('render.pass.scene', (e) => this.render(e));
        viewer.addEventListener('scene_changed', this.onSceneChange.bind(this));

        viewer.scene.addEventListener('mesh_added', this.onAdd);
        viewer.scene.addEventListener('mesh_removed', this.onRemove);
    }

    onSceneChange(e) {
        if (e.oldScene) {
            e.oldScene.removeEventListeners('mesh_added', this.onAdd);
            e.oldScene.removeEventListeners('mesh_removed', this.onRemove);
        }

        e.scene.addEventListener('mesh_added', this.onAdd);
        e.scene.addEventListener('mesh_removed', this.onRemove);
    }

    startInsertion(args, customCallback) {
        let mesh = args.mesh;
        this.dispatchEvent({
            type: 'start_inserting_mesh',
            mesh: mesh,
        });

        this.viewer.scene.addMesh(mesh);
        this.scene.add(mesh);

        let cancel = {
            callback: null,
        };

        let drag = async (e) => {
            let camera = this.viewer.scene.getActiveCamera();
            let I = Utils.getMousePointCloudIntersection(e.drag.end, camera, this.viewer, this.viewer.scene.pointclouds, {
                pickClipped: true,
                pickInvisible: false,
            });
            if (!I && args.ifcHelper) {
                let ifcPoint = await args.ifcHelper.castRayIfc();
                if (ifcPoint) {
                    I = { location: ifcPoint.point };
                }
            }
            const volumeArray = [...this.viewer.scene.volumes, ...this.viewer.scene.planes];

            if (!I && volumeArray.length > 0) {
                I = Utils.getMeshesIntersectionWithVolumes(e.drag.end, camera, this.viewer, volumeArray, {
                    newMesh: mesh.uuid,
                    pickInvisible: false,
                });
            }

            if (!I) {
                I = Utils.getMeshesIntersectionWithCesium(e.drag.end, args.toScene, args.cesiumViewer, args.facilityCords);
            }

            if (I) {
                mesh.position.copy(I.location);
            }
        };

        let drop = (e) => {
            mesh.removeEventListener('drag', drag);
            mesh.removeEventListener('drop', drop);

            cancel.callback(e);
        };

        cancel.callback = (e) => {
            mesh.removeEventListener('drag', drag);
            mesh.removeEventListener('drop', drop);
            this.viewer.removeEventListener('cancel_insertions', cancel.callback);

            if (e.button === THREE.MOUSE.LEFT) {
                //when adding a mesh
            } else if (e.button === THREE.MOUSE.RIGHT) {
                this.viewer.scene.removeMesh(mesh);
                this.scene.remove(mesh);
            }

            if (customCallback) {
                customCallback(e.drag.object);
            }
        };

        mesh.addEventListener('drag', drag);
        mesh.addEventListener('drop', drop);
        this.viewer.addEventListener('cancel_insertions', cancel.callback);

        this.viewer.inputHandler.startDragging(mesh);

        return mesh;
    }

    update() {
        if (!this.viewer.scene) {
            return;
        }
    }

    render(params) {
        const renderer = this.viewer.renderer;

        const oldTarget = renderer.getRenderTarget();

        if (params.renderTarget) {
            renderer.setRenderTarget(params.renderTarget);
        }
        renderer.render(this.scene, this.viewer.scene.getActiveCamera());
        renderer.setRenderTarget(oldTarget);
    }
}
